import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { feeAllocationActions as slice } from "../slices/feeAllocation";
import { getErrorMessage } from "../../utils/errorUtils";
import { StudentFeeAllocationRequestDto } from "../../shared/dtos/studentFeeAllocation/StudentFeeAllocationRequestDto";
import { StudentFeeAllocationResponseDto } from "../../shared/dtos/studentFeeAllocation/StudentFeeAllocationResponseDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export interface FeeAllocationQueryParams {
  page?: number;
  size?: number;
  branchId?: string;
  studentId?: string;
  academicYearId?: string;
  voteHeadId?: string;
}

export const getFeeAllocations =
  (params: FeeAllocationQueryParams = { page: 1, size: 12 }) =>
  async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingFeeAllocations());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<StudentFeeAllocationResponseDto>>
      >("/student-fee-allocations", {
        headers: { requiresAuth: true },
        params,
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(slice.setFeeAllocations(data.data.content));
        dispatch(slice.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(slice.setErrorLoadingFeeAllocations(getErrorMessage(error)));
    }
  };

export const createFeeAllocation =
  (values: StudentFeeAllocationRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingFeeAllocationCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<StudentFeeAllocationResponseDto>
      >("/student-fee-allocations", values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Fee allocated successfully!");
        dispatch(slice.createFeeAllocationSuccess(data.data));
        dispatch(
          slice.setServerResponseStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(slice.setErrorCreatingFeeAllocation(message));
    }
  };

export const deleteFeeAllocation =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingFeeAllocationDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/student-fee-allocations/${id}`,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Allocation removed successfully!");
        dispatch(slice.deleteFeeAllocationSuccess(id));
        dispatch(
          slice.setServerResponseStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(slice.setErrorDeletingFeeAllocation(message));
    }
  };

export const resetFeeAllocationServerStatus = () => (dispatch: AppDispatch) =>
  dispatch(slice.setServerResponseStatus(null));
